import { useCallback } from 'react';
import {
  TaskListElement,
  TaskWithAssigneesAndProject,
} from 'types/store/tasksList';
import { TASK_STATUS } from 'utils/constants';
import { formatDate } from 'Pages/TaskDetailPage/TaskDetailForm/helpers/helpers';
import { useAppThunkDispatch } from 'state/store';
import { updateTaskInList } from 'state/TasksList/myTasks/myTasksSlice';
import { showNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import {
  setUpdateTaskStatus,
  updateTaskDetail,
} from 'state/TaskDetail/taskSlice';
import intl from 'react-intl-universal';
import { StatusOption } from 'Atoms/EditableTableRowCell/StatusTableRowCell/StatusTableRowCell';

interface UseUpdateCellsReturn {
  handleSaveTaskStatus: (
    newValue: string,
    canUpdateStatus: boolean,
    task: TaskWithAssigneesAndProject
  ) => void;
  getStatuses: (task: TaskWithAssigneesAndProject) => StatusOption[];
}

export const useUpdateStatusCell = (): UseUpdateCellsReturn => {
  const dispatch = useAppThunkDispatch();

  const updateTaskInListAction = useCallback(
    (taskId: string, updatedTask: Partial<TaskListElement>) => {
      dispatch(
        updateTaskInList({
          taskId,
          updatedTask,
        })
      );
    },
    [dispatch]
  );

  const handleSaveTaskStatus = useCallback(
    async (
      newValue: string,
      canUpdateStatus: boolean,
      task: TaskWithAssigneesAndProject
    ) => {
      const { id } = task;
      if (!canUpdateStatus || newValue === task.status) return;

      try {
        const taskToUpdate: {
          id: string;
          status: string;
          completionDate?: string;
        } = {
          id,
          status: newValue,
        };

        if (
          newValue === TASK_STATUS.COMPLETED &&
          task.status !== TASK_STATUS.COMPLETED
        ) {
          taskToUpdate.completionDate = formatDate(new Date());
        }

        const res = await dispatch(
          updateTaskDetail({
            task: taskToUpdate,
          })
        ).unwrap();

        const taskUpdates: Partial<TaskListElement> = {
          ...res,
          assignees: res.assignees?.map((assignee) => assignee.id) || [],
          projectId: res.project?.id || '',
        };

        updateTaskInListAction(id, taskUpdates);

        if (
          newValue === TASK_STATUS.COMPLETED &&
          task.status !== TASK_STATUS.COMPLETED &&
          taskToUpdate.completionDate
        ) {
          dispatch(
            showNotification({
              notificationVariant: 'success',
              notificationTitle: intl.get(
                'TASKS.TASK_DETAIL_PAGE.COMPLETION_TASK_TOOLTIP_TITLE'
              ),
              notificationMessage: intl.get(
                'TASKS.TASK_DETAIL_PAGE.COMPLETION_DATE_AUTO_UPDATED'
              ),
            })
          );
        }
        dispatch(setUpdateTaskStatus(undefined));
      } catch (error) {
        dispatch(setUpdateTaskStatus(undefined));
        dispatch(
          showNotification({
            notificationVariant: 'error',
            notificationTitle: intl.get('TASKS.TASK_STATUS_UPDATE_ERROR.TITLE'),
            notificationMessage: intl.get(
              'TASKS.TASK_STATUS_UPDATE_ERROR.MESSAGE'
            ),
          })
        );
      }
    },
    [dispatch, updateTaskInListAction]
  );

  const getStatuses = useCallback((task: TaskWithAssigneesAndProject) => {
    return Object.values(TASK_STATUS).map((status) => ({
      label: intl.get(
        `TASKS.TASK_DETAIL_PAGE.STATUS_OPTIONS.${status.toUpperCase()}`
      ),
      value: status,
      disabled: status === TASK_STATUS.COMPLETED && task.assignees.length === 0,
      message: intl.get('TASKS.TASK_ASSIGNEE_REQUIRED_TOOLTIP'),
    }));
  }, []);

  return {
    handleSaveTaskStatus,
    getStatuses,
  };
};
